import React, { useEffect, useMemo, useRef, useState } from "react";
import "./admin.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

function formatBytes(bytes) {
  const num = Number(bytes || 0);
  if (!num) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(num) / Math.log(1024)), units.length - 1);
  const value = num / 1024 ** i;

  return `${value.toFixed(value >= 100 || i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatDate(value) {
  if (!value) return "—";

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(d);
}

function getReadableError(data, fallback = "Something went wrong") {
  if (!data) return fallback;
  if (typeof data === "string") return data;
  if (typeof data.error === "string") return data.error;
  if (typeof data.error?.message === "string") return data.error.message;
  if (typeof data.message === "string") return data.message;

  try {
    return JSON.stringify(data);
  } catch {
    return fallback;
  }
}

function formatPartsDebug(debug) {
  if (!debug) return "";

  const pairs = [
    ["lines", debug.lineCount],
    ["pipe", debug.pipeLineCount],
    ["tabs", debug.tabLineCount],
    ["htmlLines", debug.htmlLineCount],
    ["htmlRows", debug.htmlRowCount],
    ["headers", debug.headerCount],
    ["headered", debug.headeredTableRows],
    ["headerless", debug.headerlessTableRows],
    ["plain", debug.plainRows],
    ["trailingPlain", debug.trailingPlainRows],
    ["groups", debug.groupHeadingCount],
    ["sections", debug.sectionHeadingCount],
    ["indexSkipped", debug.skippedIndexLines],
  ]
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([label, value]) => `${label}: ${value}`)
    .join(", ");

  const rejected = Array.isArray(debug.sampleRejectedTableLines)
    ? debug.sampleRejectedTableLines
        .slice(0, 2)
        .map((line) => String(line || "").slice(0, 180))
        .filter(Boolean)
        .join(" || ")
    : "";

  return rejected ? `${pairs}. Rejected sample: ${rejected}` : pairs;
}

function formatPartsExtractMessage(data, fallback) {
  if (data?.warning) return data.warning;

  const partsCount = Number(data?.partsCount || 0);
  if (partsCount > 0) return fallback;

  const debug = formatPartsDebug(data?.partsDebug);
  return debug ? `${fallback}. Parser debug: ${debug}` : fallback;
}

function formatPartsZeroMessage(data, fallback) {
  if (Number(data?.partsCount || 0) > 0) return fallback;

  const parserVersion = data?.partsParserVersion || "no parser version in response";
  const keys = data && typeof data === "object" ? Object.keys(data).join(", ") : "no JSON body";
  return formatPartsExtractMessage(
    data,
    `${fallback}. Parser marker: ${parserVersion}. Response keys: ${keys}`
  );
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function prettyStatus(status) {
  switch ((status || "").toLowerCase()) {
    case "uploaded":
      return "Uploaded";
    case "processing":
      return "Processing";
    case "processed":
      return "Ready to review";
    case "publishing":
      return "Publishing";
    case "published":
      return "Published";
    case "failed":
      return "Failed";
    default:
      return "Draft";
  }
}

function getStatusClass(status) {
  switch ((status || "").toLowerCase()) {
    case "uploaded":
    case "processing":
    case "publishing":
      return "jp-admin-badge jp-admin-badge--pending";
    case "processed":
      return "jp-admin-badge jp-admin-badge--paid";
    case "published":
      return "jp-admin-badge jp-admin-badge--room";
    case "failed":
      return "jp-admin-badge jp-admin-badge--cancelled";
    default:
      return "jp-admin-badge";
  }
}

function fileTypeLabel(type) {
  switch ((type || "").toLowerCase()) {
    case "manual":
      return "Manual";
    case "parts":
      return "Parts list";
    default:
      return "Document";
  }
}

function buildChunkCount(job) {
  if (typeof job?.chunkCount === "number") return job.chunkCount;
  if (Array.isArray(job?.chunks)) return job.chunks.length;
  return 0;
}

function buildPageCount(job) {
  if (typeof job?.pageCount === "number") return job.pageCount;
  return 0;
}

function isPartsListJob(job) {
  return String(job?.fileType || "").toLowerCase() === "parts";
}

function buildPartsCount(job) {
  if (typeof job?.partsCount === "number") return job.partsCount;
  return 0;
}

function normaliseJob(row) {
  return {
    id: row.id,
    fileName: row.fileName ?? row.file_name ?? "—",
    fileType: row.fileType ?? row.file_type ?? "manual",
    fileSize: Number(row.fileSize ?? row.file_size ?? 0),
    status: row.status ?? "uploaded",
    errorMessage: row.errorMessage ?? row.error_message ?? "",
    createdAt: row.createdAt ?? row.created_at ?? null,
    updatedAt: row.updatedAt ?? row.updated_at ?? null,
    pageCount: Number(row.pageCount ?? row.page_count ?? 0),
    chunkCount: Number(row.chunkCount ?? row.chunk_count ?? 0),
    partsCount: Number(row.partsCount ?? row.parts_count ?? 0),
    markdownPreview: row.markdownPreview ?? row.markdown ?? "",
    chunks: Array.isArray(row.chunks)
      ? row.chunks
      : Array.isArray(row.chunks_json)
      ? row.chunks_json
      : [],
    manufacturer: row.manufacturer ?? "",
    model: Array.isArray(row.model) ? row.model : [],
    year: row.year ?? "",
    section: row.section ?? "",
    publishStage: row.publishStage ?? row.publish_stage ?? "",
    publishCompleted: Number(row.publishCompleted ?? row.publish_completed ?? 0),
    publishTotal: Number(row.publishTotal ?? row.publish_total ?? 0),
  };
}

function normaliseVisuals(rawVisuals) {
  if (!Array.isArray(rawVisuals)) return [];

  return rawVisuals.map((visual, index) => ({
    id: visual?.id ?? `visual-${index}`,
    title: visual?.title ?? "Diagram",
    pageNumber: Number(visual?.pageNumber ?? visual?.page_number ?? 0),
    caption: visual?.caption ?? "",
    visualType: visual?.visualType ?? visual?.visual_type ?? "diagram",
    imageUrl: visual?.imageUrl ?? visual?.image_url ?? "",
    imageS3Key: visual?.imageS3Key ?? visual?.image_s3_key ?? "",
    width: visual?.width ?? null,
    height: visual?.height ?? null,
  }));
}

function stripBrandPrefix(name) {
  return String(name || "")
    .replace(/^Matchless\s+/i, "")
    .replace(/^AJS\s+/i, "")
    .replace(/^WD\s+/i, "")
    .trim();
}

function buildUniqueModelOptions(rows) {
  const seen = new Set();

  return (rows || [])
    .map((row) => stripBrandPrefix(row?.name))
    .filter(Boolean)
    .filter((name) => {
      const key = name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => a.localeCompare(b));
}

function parseModelInput(value) {
  return String(value || "")
    .split(",")
    .map((x) => x.trim().toUpperCase())
    .filter(Boolean);
}

function formatModelInput(models) {
  return (models || []).join(", ");
}

async function hydrateVisualsWithSignedUrls(rawVisuals) {
  const visuals = normaliseVisuals(rawVisuals);
  const keys = visuals.map((v) => v.imageS3Key).filter(Boolean);

  if (!keys.length) return visuals;

  const res = await fetch("/.netlify/functions/manual-visual-signed-urls", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ keys }),
  });

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new Error(getReadableError(data, "Failed to sign URLs"));
  }

  const urlMap = new Map((data?.results || []).map((x) => [x.key, x.url]));

  return visuals.map((v) => ({
    ...v,
    imageUrl: urlMap.get(v.imageS3Key) || "",
  }));
}

export default function AdminManualsPage() {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const [loginSubmitting, setLoginSubmitting] = useState(false);

  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedJobId, setSelectedJobId] = useState(null);

  const [fileType, setFileType] = useState("manual");
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const [manufacturer, setManufacturer] = useState("Matchless");
  const [modelInput, setModelInput] = useState("G3L");
  const [bikeOptions, setBikeOptions] = useState([]);
  const [modelSuggestionsOpen, setModelSuggestionsOpen] = useState(false);
  const [yearInput, setYearInput] = useState("1945");
  const [section, setSection] = useState("Technical Data Text");

  const [uploading, setUploading] = useState(false);
  const [ocring, setOcring] = useState(false);
  const [chunking, setChunking] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [message, setMessage] = useState("");
  const [previewMode, setPreviewMode] = useState("markdown");
  const [extractVisuals, setExtractVisuals] = useState([]);

  const fileInputRef = useRef(null);

  const parsedModels = useMemo(() => parseModelInput(modelInput), [modelInput]);

  const filteredBikeOptions = useMemo(() => {
    const selected = new Set(parsedModels.map((x) => x.toLowerCase()));
    const lastToken =
      String(modelInput || "")
        .split(",")
        .pop()
        ?.trim()
        .toLowerCase() || "";

    return bikeOptions
      .filter((option) => !selected.has(option.toLowerCase()))
      .filter((option) =>
        !lastToken ? true : option.toLowerCase().includes(lastToken)
      )
      .slice(0, 12);
  }, [bikeOptions, modelInput, parsedModels]);

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      setAuthLoading(true);
      setAuthError("");

      try {
        const res = await fetch("/.netlify/functions/admin-session", {
          credentials: "include",
        });

        if (!cancelled) {
          setIsAuthed(res.ok);
        }
      } catch {
        if (!cancelled) {
          setIsAuthed(false);
        }
      } finally {
        if (!cancelled) {
          setAuthChecked(true);
          setAuthLoading(false);
        }
      }
    }

    checkSession();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function handleOutsideClick() {
      setModelSuggestionsOpen(false);
    }

    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadBikeOptions() {
      try {
        const res = await fetch("/.netlify/functions/admin-bike-options", {
          credentials: "include",
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load bike options");
        }

        if (!cancelled) {
          setBikeOptions(buildUniqueModelOptions(data?.bikes || []));
        }
      } catch (err) {
        console.error("Failed to load bike options:", err);
        if (!cancelled) {
          setBikeOptions([]);
        }
      }
    }

    if (isAuthed) {
      loadBikeOptions();
    }

    return () => {
      cancelled = true;
    };
  }, [isAuthed]);

  useEffect(() => {
    let cancelled = false;

    async function loadVisualsForSelectedJob() {
      if (!selectedJobId) {
        setExtractVisuals([]);
        return;
      }

      try {
        const res = await fetch(
          `/.netlify/functions/manual-get-visuals?jobId=${encodeURIComponent(selectedJobId)}`
        );

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(data?.error || "Failed to load visuals");
        }

        const hydrated = await hydrateVisualsWithSignedUrls(data?.visuals);

        if (!cancelled) {
          setExtractVisuals(hydrated);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setExtractVisuals([]);
        }
      }
    }

    loadVisualsForSelectedJob();

    return () => {
      cancelled = true;
    };
  }, [selectedJobId]);

  useEffect(() => {
    if (!isAuthed) {
      setJobs([]);
      setLoadingJobs(false);
      return;
    }

    reloadJobs();
  }, [isAuthed]);

  const liveSelectedJob = useMemo(() => {
    return jobs.find((job) => job.id === selectedJobId) || null;
  }, [jobs, selectedJobId]);

  const filteredJobs = useMemo(() => {
    const q = search.trim().toLowerCase();

    return jobs.filter((job) => {
      const matchesSearch =
        !q ||
        String(job.fileName || "").toLowerCase().includes(q) ||
        String(job.id || "").toLowerCase().includes(q) ||
        String(job.fileType || "").toLowerCase().includes(q) ||
        String(job.manufacturer || "").toLowerCase().includes(q) ||
        String(job.model || []).join(" ").toLowerCase().includes(q);

      const matchesStatus =
        statusFilter === "all" ||
        String(job.status || "").toLowerCase() === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [jobs, search, statusFilter]);

  const selectedJob = useMemo(() => {
    return jobs.find((job) => job.id === selectedJobId) || null;
  }, [jobs, selectedJobId]);

  const visualsByPage = useMemo(() => {
    const grouped = {};

    for (const visual of extractVisuals) {
      const pageNumber = Number(visual.pageNumber || 0);
      if (!pageNumber) continue;
      if (!grouped[pageNumber]) grouped[pageNumber] = [];
      grouped[pageNumber].push(visual);
    }

    return grouped;
  }, [extractVisuals]);

  const visualMap = useMemo(() => {
    const map = new Map();

    for (const v of extractVisuals) {
      if (v.title) {
        map.set(v.title.toLowerCase(), v.imageUrl);
      }
    }

    return map;
  }, [extractVisuals]);

  const previewContent = useMemo(() => {
    if (!selectedJob) return "";

    if (previewMode !== "markdown") {
      return JSON.stringify(selectedJob.chunks || [], null, 2);
    }

    const rawMarkdown = selectedJob.markdownPreview || "";
    if (!rawMarkdown) return "";

    const lines = rawMarkdown.split("\n");
    const output = [];

    for (const line of lines) {
      output.push(line);

      const pageMatch = line.match(/^# Page (\d+)/i);
      if (!pageMatch) continue;

      const pageNumber = Number(pageMatch[1]);
      const visuals = visualsByPage[pageNumber];

      if (!visuals?.length) continue;

      output.push("");

      for (const visual of visuals) {
        if (visual.imageUrl) {
          output.push(`![${visual.title || "Diagram"}](${visual.imageUrl})`);
          output.push("");
        }

        if (visual.caption) {
          output.push(`*${visual.caption}*`);
          output.push("");
        }
      }
    }

    return output.join("\n");
  }, [selectedJob, previewMode, visualsByPage]);

  const publishProgressText = useMemo(() => {
    if (!liveSelectedJob) return "";

    const completed = Number(liveSelectedJob.publishCompleted || 0);
    const total = Number(liveSelectedJob.publishTotal || 0);

    return total > 0 ? `${completed}/${total}` : "";
  }, [liveSelectedJob]);

  async function reloadJobs(preferredJobId = null) {
    if (!isAuthed) return;

    setLoadingJobs(true);
    setLoadError("");

    try {
      const res = await fetch("/.netlify/functions/admin-manual-jobs", {
        credentials: "include",
      });

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(json?.error || "Failed to load manual jobs");
      }

      const nextJobs = Array.isArray(json) ? json.map(normaliseJob) : [];
      setJobs(nextJobs);

      const preferredStillExists =
        preferredJobId && nextJobs.some((job) => job.id === preferredJobId);

      const currentStillExists =
        selectedJobId && nextJobs.some((job) => job.id === selectedJobId);

      if (preferredStillExists) {
        setSelectedJobId(preferredJobId);
      } else if (currentStillExists) {
        setSelectedJobId(selectedJobId);
      } else if (nextJobs.length > 0) {
        setSelectedJobId(nextJobs[0].id);
      } else {
        setSelectedJobId(null);
      }
    } catch (e) {
      setLoadError(e?.message || "Failed to load manual jobs");
    } finally {
      setLoadingJobs(false);
    }
  }

  async function processVisuals(jobId, onProgress) {
    let done = false;

    while (!done) {
      const res = await fetch("/.netlify/functions/manual-process-visuals", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ jobId }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || "Visual processing failed");
      }

      done = data?.done === true;

      if (onProgress) {
        onProgress(data);
      }

      if (data?.retry) {
        await sleep(Number(data.retryAfterMs || 10000));
        continue;
      }

      if (!done) {
        await sleep(2000);
      }
    }
  }

  async function processParts(jobId, onProgress) {
    let done = false;

    while (!done) {
      const res = await fetch("/.netlify/functions/manual-process-parts", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ jobId }),
      });

      const text = await res.text();
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = { error: text || "Parts extraction returned a non-JSON response" };
      }

      if (!res.ok) {
        throw new Error(data?.error || "Parts extraction failed");
      }

      done = data?.done === true;

      if (onProgress) {
        onProgress(data);
      }

      if (data?.retry) {
        await sleep(Number(data.retryAfterMs || 10000));
        continue;
      }

      if (!done) {
        await sleep(2000);
      }
    }
  }

  async function processDiagramSegments(jobId, onProgress) {
    let done = false;

    while (!done) {
      const res = await fetch("/.netlify/functions/manual-segment-diagrams", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ jobId }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || "Diagram segmentation failed");
      }

      done = data?.done === true;

      if (onProgress) {
        onProgress(data);
      }

      if (!done) {
        await sleep(800);
      }
    }
  }

  function addModel(model) {
    const next = [...parsedModels];

    if (!next.some((x) => x.toLowerCase() === model.toLowerCase())) {
      next.push(model.toUpperCase());
    }

    setModelInput(formatModelInput(next));
    setModelSuggestionsOpen(false);
  }

  function removeModel(modelToRemove) {
    const next = parsedModels.filter(
      (x) => x.toLowerCase() !== modelToRemove.toLowerCase()
    );

    setModelInput(formatModelInput(next));
  }

  function handleModelInputChange(value) {
    setModelInput(value);
    setModelSuggestionsOpen(true);
  }

  async function handleLogin(e) {
    e.preventDefault();
    setAuthError("");
    setLoginSubmitting(true);

    try {
      const res = await fetch("/.netlify/functions/admin-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ password }),
      });

      const json = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(json?.error || "Login failed");
      }

      setIsAuthed(true);
      setPassword("");
    } catch (e) {
      setAuthError(e?.message || "Login failed");
    } finally {
      setLoginSubmitting(false);
    }
  }

  async function handleLogout() {
    try {
      await fetch("/.netlify/functions/admin-logout", {
        method: "POST",
        credentials: "include",
      });
    } catch {
      //
    }

    setIsAuthed(false);
    setSelectedJobId(null);
    setJobs([]);
    setExtractVisuals([]);
    setMessage("");
    setLoadError("");
  }

  function handlePickedFiles(files) {
    const file = files?.[0];
    if (!file) return;

    setSelectedFile(file);
    setMessage("");
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragActive(false);
    handlePickedFiles(e.dataTransfer.files);
  }

  async function handleUpload() {
    if (!selectedFile) return;

    const cleanManufacturer = String(manufacturer || "").trim();
    const cleanSection = String(section || "").trim();
    const parsedYear = Number(yearInput);

    if (!cleanManufacturer) {
      setMessage("Please enter a manufacturer.");
      return;
    }

    if (!parsedModels.length) {
      setMessage("Please enter at least one model.");
      return;
    }

    if (!Number.isFinite(parsedYear)) {
      setMessage("Please enter a valid year.");
      return;
    }

    if (!cleanSection) {
      setMessage("Please enter a section name.");
      return;
    }

    setUploading(true);
    setMessage("");
    setExtractVisuals([]);

    try {
      const res = await fetch("/.netlify/functions/manual-create-upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileName: selectedFile.name,
          fileType,
          fileSize: selectedFile.size,
          manufacturer: cleanManufacturer,
          model: parsedModels,
          year: parsedYear,
          section: cleanSection,
        }),
      });

      const uploadMeta = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(uploadMeta?.error || "Failed to get upload URL");
      }

      const { uploadUrl, jobId } = uploadMeta || {};

      if (!uploadUrl || !jobId) {
        throw new Error("Upload setup was incomplete");
      }

      const uploadRes = await fetch(uploadUrl, {
        method: "PUT",
        body: selectedFile,
      });

      if (!uploadRes.ok) {
        throw new Error("Upload failed");
      }

      setUploading(false);
      setMessage("Upload complete. Running OCR…");
      setOcring(true);

      const extractRes = await fetch("/.netlify/functions/manual-extract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId }),
      });

      const extractJson = await extractRes.json().catch(() => null);

      if (fileType === "parts") {
        console.info("manual-extract parts response", extractJson);
      }

      if (!extractRes.ok) {
        throw new Error(extractJson?.error || "OCR failed");
      }

      setMessage(
        fileType === "parts"
          ? `OCR complete${
              extractJson?.visualCount ? ` • ${extractJson.visualCount} visuals found` : ""
            }. Preparing parts extraction...`
          : `OCR complete${
              extractJson?.visualCount ? ` • ${extractJson.visualCount} visuals found` : ""
            }. Uploading visuals...`
      );

      await processVisuals(jobId, (data) => {
        setMessage(`Uploading visuals: ${data.completed}/${data.total}`);
      });

      if (fileType === "parts") {
        await processParts(jobId, (data) => {
          setMessage(
            `Extracting parts: ${data.completed}/${data.total} • ${data.partsCount || 0} rows`
          );
        });

        await processDiagramSegments(jobId, (data) => {
          const stage =
            data.stage === "part-crop-ocr" ? "OCR matching part crops" : "Segmenting diagrams";
          setMessage(
            `${stage}: ${data.completed}/${data.total} • ${data.insertedCount || 0} crops`
          );
        });
      }

      await reloadJobs(jobId);
      setSelectedJobId(jobId);
      setPreviewMode("markdown");
      setMessage(
        fileType === "parts"
          ? "Upload + OCR + parts extraction complete"
          : "Upload + OCR + visual processing complete"
      );
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (e) {
      console.error(e);
      setMessage(e?.message || "Upload failed");
    } finally {
      setUploading(false);
      setOcring(false);
    }
  }

  async function handleExtract(job) {
    if (!job) return;

    setExtracting(true);
    setMessage("");
    setExtractVisuals([]);

    try {
      const res = await fetch("/.netlify/functions/manual-extract", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId: job.id }),
      });

      const data = await res.json().catch(() => null);

      if (isPartsListJob(job)) {
        console.info("manual-extract parts response", data);
      }

      if (!res.ok) {
        throw new Error(data?.error || "Extraction failed");
      }

      setMessage(
        isPartsListJob(job)
          ? `OCR extraction complete for ${job.fileName}${
              data?.visualCount ? ` • ${data.visualCount} visuals found` : ""
            }. Preparing parts extraction...`
          : `OCR extraction complete for ${job.fileName}${
              data?.visualCount ? ` • ${data.visualCount} visuals found` : ""
            }. Uploading visuals...`
      );

      await processVisuals(job.id, (progress) => {
        setMessage(`Uploading visuals: ${progress.completed}/${progress.total}`);
      });

      if (isPartsListJob(job)) {
        await processParts(job.id, (progress) => {
          setMessage(
            `Extracting parts: ${progress.completed}/${progress.total} • ${progress.partsCount || 0} rows`
          );
        });

        await processDiagramSegments(job.id, (progress) => {
          const stage =
            progress.stage === "part-crop-ocr"
              ? "OCR matching part crops"
              : "Segmenting diagrams";
          setMessage(
            `${stage}: ${progress.completed}/${progress.total} • ${progress.insertedCount || 0} crops`
          );
        });
      }

      await reloadJobs(job.id);
      setSelectedJobId(job.id);
      setPreviewMode("markdown");
      setMessage(
        isPartsListJob(job)
          ? `OCR + parts extraction complete for ${job.fileName}`
          : `OCR + visual processing complete for ${job.fileName}`
      );
    } catch (e) {
      setMessage(e?.message || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  }

  async function handleCreateChunks(job) {
    if (!job) return;

    if (isPartsListJob(job)) {
      setMessage("Parts lists are added to the lookup during OCR extraction and do not need chunks.");
      return;
    }

    setChunking(true);
    setMessage("");

    try {
      const res = await fetch("/.netlify/functions/manual-chunk", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ jobId: job.id }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || "Chunk generation failed");
      }

      await reloadJobs(job.id);
      setSelectedJobId(job.id);
      setPreviewMode("chunks");
      setMessage(`Created ${data?.chunkCount || 0} chunks for ${job.fileName}.`);
    } catch (e) {
      setMessage(e?.message || "Chunk generation failed");
    } finally {
      setChunking(false);
    }
  }

  async function handlePublish(job) {
    if (!job) return;

    if (isPartsListJob(job)) {
      setMessage("Parts lists are lookup-only and are not published to AI search.");
      return;
    }

    setPublishing(true);
    setMessage("");

    try {
      let done = false;

      while (!done) {
        const res = await fetch("/.netlify/functions/manual-publish", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ jobId: job.id }),
        });

        const data = await res.json().catch(() => null);

        if (!res.ok) {
          throw new Error(getReadableError(data, "Publish failed"));
        }

        await reloadJobs(job.id);
        setSelectedJobId(job.id);

        done = data?.done === true;

        setMessage(
          done
            ? `Published ${data?.total ?? data?.uploadedCount ?? 0} chunks.`
            : `Progress: ${data?.completed ?? 0}/${data?.total ?? 0}`
        );

        if (!done) {
          await sleep(750);
        }
      }
    } catch (e) {
      console.error("Publish loop failed:", e);
      setMessage(e?.message || "Publish failed");
    } finally {
      setPublishing(false);
    }
  }

  async function handleDelete(job) {
    if (!job?.id) return;

    const confirmed = window.confirm(
      `Delete "${job.fileName}"?\n\nThis will permanently delete the PDF, extracted images, chunk rows, visual rows, and the manual record.`
    );

    if (!confirmed) return;

    setDeleting(true);
    setMessage("");

    try {
      const res = await fetch("/.netlify/functions/manual-delete", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ jobId: job.id }),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        throw new Error(data?.error || "Delete failed");
      }

      const deletedId = job.id;

      setExtractVisuals([]);
      setSelectedJobId((current) => (current === deletedId ? null : current));

      await reloadJobs();
      setMessage(`Deleted ${job.fileName}`);
    } catch (e) {
      console.error("Delete failed:", e);
      setMessage(e?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  }

  const showOverlay = uploading || ocring || extracting || chunking || publishing || deleting;

  return (
    <div className="jp-admin-page">
      {authLoading && !authChecked ? (
        <div className="jp-admin-panel">Checking access…</div>
      ) : !isAuthed ? (
        <div className="jp-admin-login-wrap">
          <div className="jp-admin-login-card">
            <h1 className="jp-admin-page__title">Manual admin</h1>
            <p className="jp-admin-page__subtitle">
              Enter the admin password to upload manuals and publish OCR chunks.
            </p>

            <form onSubmit={handleLogin} className="jp-admin-login-form">
              <input
                type="password"
                className="jp-admin-input"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />

              <button type="submit" className="jp-admin-btn" disabled={loginSubmitting}>
                {loginSubmitting ? "Signing in…" : "Sign in"}
              </button>
            </form>

            {authError ? <div className="jp-admin-message">{authError}</div> : null}
          </div>
        </div>
      ) : (
        <>
          <div className="jp-admin-page__header">
            <div>
              <h1 className="jp-admin-page__title">Document ingestion</h1>
              <p className="jp-admin-page__subtitle">
                Upload a manual or parts list, run OCR extraction, then publish manual
                chunks or add parts rows to the lookup.
              </p>
            </div>

            <button
              type="button"
              className="jp-admin-btn jp-admin-btn--secondary"
              onClick={handleLogout}
            >
              Log out
            </button>
          </div>

          <section className="jp-admin-dashboard">
            <div className="jp-admin-stats-grid">
              <div className="jp-admin-stat-card">
                <div className="jp-admin-stat-label">Documents</div>
                <div className="jp-admin-stat-value">{jobs.length}</div>
              </div>

              <div className="jp-admin-stat-card">
                <div className="jp-admin-stat-label">Ready to review</div>
                <div className="jp-admin-stat-value">
                  {jobs.filter((j) => String(j.status || "").toLowerCase() === "processed").length}
                </div>
              </div>

              <div className="jp-admin-stat-card">
                <div className="jp-admin-stat-label">Published</div>
                <div className="jp-admin-stat-value">
                  {jobs.filter((j) => String(j.status || "").toLowerCase() === "published").length}
                </div>
              </div>

              <div className="jp-admin-stat-card">
                <div className="jp-admin-stat-label">Total chunks</div>
                <div className="jp-admin-stat-value">
                  {jobs.reduce((sum, job) => sum + buildChunkCount(job), 0)}
                </div>
              </div>
            </div>
          </section>

        <section
  className="jp-admin-panel"
  style={{
    marginBottom: 20,
    overflow: "visible",
    position: "relative",
    zIndex: 50,
  }}
>
            <div style={{ display: "grid", gap: 18 }}>
              <div>
                <h2 className="jp-admin-chart-title" style={{ marginBottom: 8 }}>
                  1. Upload document
                </h2>
                <p className="jp-admin-muted" style={{ marginBottom: 16 }}>
                  Upload a PDF manual or parts list, then run OCR extraction once the
                  upload has completed.
                </p>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 12,
                  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
                }}
              >
                <input
                  type="text"
                  className="jp-admin-input"
                  placeholder="Manufacturer"
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                  disabled={showOverlay}
                />

              <div
  style={{ position: "relative", zIndex: 1000 }}
  onClick={(e) => e.stopPropagation()}
>
                  <input
                    type="text"
                    className="jp-admin-input"
                    placeholder="Model(s) e.g. G3L, G3WO"
                    value={modelInput}
                    onChange={(e) => handleModelInputChange(e.target.value)}
                    onFocus={() => setModelSuggestionsOpen(true)}
                    disabled={showOverlay}
                  />

                  {parsedModels.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        gap: 8,
                        flexWrap: "wrap",
                        marginTop: 8,
                      }}
                    >
                      {parsedModels.map((model) => (
                        <button
                          key={model}
                          type="button"
                          onClick={() => removeModel(model)}
                          disabled={showOverlay}
                          style={{
                            border: "1px solid rgba(148, 163, 184, 0.35)",
                            background: "rgba(37, 99, 235, 0.08)",
                            color: "#1e3a8a",
                            borderRadius: 999,
                            padding: "0.3rem 0.65rem",
                            fontSize: ".85rem",
                            cursor: "pointer",
                          }}
                          title="Remove model"
                        >
                          {model} ×
                        </button>
                      ))}
                    </div>
                  ) : null}

{modelSuggestionsOpen &&
filteredBikeOptions.length > 0 &&
!showOverlay ? (
  <div
    style={{
      position: "absolute",
      top: "calc(100% + 6px)",
      left: 0,
      right: 0,
      zIndex: 9999,
      background: "#fff",
      border: "1px solid rgba(148, 163, 184, 0.25)",
      borderRadius: 12,
      boxShadow: "0 12px 30px rgba(15, 23, 42, 0.12)",
      maxHeight: 260,
      overflowY: "auto",
    }}
  >
    {filteredBikeOptions.map((model) => (
      <button
        key={model}
        type="button"
        onClick={() => addModel(model)}
        style={{
          display: "block",
          width: "100%",
          textAlign: "left",
          padding: "0.7rem 0.85rem",
          background: "#fff",
          border: 0,
          borderBottom: "1px solid rgba(148, 163, 184, 0.12)",
          cursor: "pointer",
        }}
      >
        {model}
      </button>
    ))}
  </div>
) : null}
                </div>

                <input
                  type="number"
                  className="jp-admin-input"
                  placeholder="Year"
                  value={yearInput}
                  onChange={(e) => setYearInput(e.target.value)}
                  disabled={showOverlay}
                />

                <input
                  type="text"
                  className="jp-admin-input"
                  placeholder="Section"
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                  disabled={showOverlay}
                />
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 12,
                  gridTemplateColumns: "220px 1fr auto",
                }}
              >
                <select
                  className="jp-admin-select"
                  value={fileType}
                  onChange={(e) => setFileType(e.target.value)}
                  disabled={showOverlay}
                >
                  <option value="manual">Manual</option>
                  <option value="parts">Parts list</option>
                </select>

                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragActive(true);
                  }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="jp-admin-panel"
                  style={{
                    cursor: "pointer",
                    border: dragActive
                      ? "2px dashed rgba(37, 99, 235, 0.75)"
                      : "2px dashed rgba(148, 163, 184, 0.45)",
                    background: dragActive ? "rgba(37, 99, 235, 0.04)" : "transparent",
                    padding: 18,
                    margin: 0,
                  }}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    style={{ display: "none" }}
                    onChange={(e) => handlePickedFiles(e.target.files)}
                  />

                  {selectedFile ? (
                    <div>
                      <div style={{ fontWeight: 700 }}>{selectedFile.name}</div>
                      <div className="jp-admin-muted">
                        {formatBytes(selectedFile.size)} • {fileTypeLabel(fileType)}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ fontWeight: 700 }}>Drop a PDF here or click to browse</div>
                      <div className="jp-admin-muted">Manuals and parts lists only for now</div>
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="jp-admin-btn"
                  onClick={handleUpload}
                  disabled={!selectedFile || showOverlay}
                >
                  {uploading ? "Uploading…" : "Upload"}
                </button>
              </div>
            </div>
          </section>

          <section className="jp-admin-toolbar">
            <input
              type="text"
              className="jp-admin-input"
              placeholder="Search by file name, id or type…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />

            <select
              className="jp-admin-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="uploaded">Uploaded</option>
              <option value="processing">Processing</option>
              <option value="processed">Processed</option>
              <option value="publishing">Publishing</option>
              <option value="published">Published</option>
              <option value="failed">Failed</option>
            </select>

            <button
              type="button"
              className="jp-admin-btn jp-admin-btn--secondary"
              onClick={() => reloadJobs(selectedJobId)}
              disabled={loadingJobs}
            >
              {loadingJobs ? "Refreshing…" : "Refresh"}
            </button>
          </section>

          <div className="jp-admin-results">
            {filteredJobs.length === jobs.length ? (
              <>
                {jobs.length} document{jobs.length === 1 ? "" : "s"}
              </>
            ) : (
              <>
                Showing {filteredJobs.length} of {jobs.length} document
                {jobs.length === 1 ? "" : "s"}
              </>
            )}
          </div>

          {message ? <div className="jp-admin-message">{message}</div> : null}
          {loadError ? <div className="jp-admin-message">{loadError}</div> : null}

          <div
            style={{
              display: "grid",
              gap: 20,
              gridTemplateColumns: "minmax(320px, 520px) minmax(0, 1fr)",
              alignItems: "start",
            }}
          >
            <section className="jp-admin-panel jp-admin-panel--table" style={{ margin: 0 }}>
              {loadingJobs ? (
                <div>Loading documents…</div>
              ) : (
                <div className="jp-admin-table-wrap">
                  <table className="jp-admin-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>File</th>
                        <th>Type</th>
                        <th>Pages</th>
                        <th>Chunks</th>
                        <th>Updated</th>
                      </tr>
                    </thead>

                    <tbody>
                      {filteredJobs.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="jp-admin-empty">
                            No documents found.
                          </td>
                        </tr>
                      ) : (
                        filteredJobs.map((job) => {
                          const isSelected = selectedJobId === job.id;

                          return (
                            <tr
                              key={job.id}
                              className="jp-admin-row"
                              onClick={() => setSelectedJobId(job.id)}
                              style={{
                                cursor: "pointer",
                                background: isSelected
                                  ? "rgba(37, 99, 235, 0.06)"
                                  : undefined,
                              }}
                            >
                              <td>
                                <span className={getStatusClass(job.status)}>
                                  {prettyStatus(job.status)}
                                </span>
                              </td>

                              <td>
                                <div style={{ fontWeight: 700 }}>{job.fileName || "—"}</div>
                                <div className="jp-admin-muted">{formatBytes(job.fileSize)}</div>
                              </td>

                              <td>{fileTypeLabel(job.fileType)}</td>
                              <td>{buildPageCount(job) || "—"}</td>
                              <td>
                                {isPartsListJob(job)
                                  ? `${buildPartsCount(job)} parts`
                                  : buildChunkCount(job) || "—"}
                              </td>
                              <td>{formatDate(job.updatedAt || job.createdAt)}</td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            <section className="jp-admin-panel" style={{ margin: 0 }}>
              {!selectedJob ? (
                <div className="jp-admin-empty">Select a document to review.</div>
              ) : (
                <div style={{ display: "grid", gap: 18 }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      alignItems: "start",
                    }}
                  >
                    <div>
                      <h2
                        className="jp-admin-page__title"
                        style={{ fontSize: "1.45rem", marginBottom: 4 }}
                      >
                        {selectedJob.fileName}
                      </h2>
                      <p className="jp-admin-page__subtitle" style={{ marginBottom: 0 }}>
                        {fileTypeLabel(selectedJob.fileType)} • {formatBytes(selectedJob.fileSize)} •{" "}
                        {prettyStatus(selectedJob.status)}
                      </p>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        type="button"
                        className="jp-admin-btn jp-admin-btn--secondary"
                        onClick={() => handleExtract(selectedJob)}
                        disabled={extracting || publishing || chunking || deleting}
                      >
                        {extracting ? "Extracting…" : "Run OCR extract"}
                      </button>

                      {!isPartsListJob(selectedJob) ? (
                        <>
                          <button
                            type="button"
                            className="jp-admin-btn jp-admin-btn--secondary"
                            onClick={() => handleCreateChunks(selectedJob)}
                            disabled={
                              chunking ||
                              extracting ||
                              publishing ||
                              deleting ||
                              !selectedJob?.markdownPreview
                            }
                          >
                            {chunking ? "Creating chunks…" : "Create chunks"}
                          </button>

                          <button
                            type="button"
                            className="jp-admin-btn"
                            onClick={() => handlePublish(selectedJob)}
                            disabled={
                              publishing ||
                              extracting ||
                              chunking ||
                              deleting ||
                              String(selectedJob.status || "").toLowerCase() !== "processed" ||
                              !buildChunkCount(selectedJob)
                            }
                          >
                            {publishing ? "Publishing…" : "Publish chunks"}
                          </button>
                        </>
                      ) : null}

                      <button
                        type="button"
                        className="jp-admin-btn jp-admin-btn--secondary"
                        onClick={() => handleDelete(selectedJob)}
                        disabled={deleting || extracting || chunking || publishing}
                        style={{
                          borderColor: "rgba(220, 38, 38, 0.35)",
                          color: "#b91c1c",
                        }}
                      >
                        {deleting ? "Deleting…" : "Delete document"}
                      </button>
                    </div>
                  </div>

                  <div className="jp-admin-detail-grid">
                    <div className="jp-admin-detail-card">
                      <h3>Document info</h3>
                      <p><strong>ID:</strong> {selectedJob.id}</p>
                      <p><strong>Uploaded:</strong> {formatDate(selectedJob.createdAt)}</p>
                      <p><strong>Updated:</strong> {formatDate(selectedJob.updatedAt)}</p>
                      {selectedJob.errorMessage ? (
                        <p><strong>Note:</strong> {selectedJob.errorMessage}</p>
                      ) : null}
                      <p><strong>Pages:</strong> {buildPageCount(selectedJob) || "—"}</p>
                      {isPartsListJob(selectedJob) ? (
                        <p><strong>Parts:</strong> {buildPartsCount(selectedJob)}</p>
                      ) : (
                        <p><strong>Chunks:</strong> {buildChunkCount(selectedJob) || "—"}</p>
                      )}
                      <p><strong>Manufacturer:</strong> {selectedJob.manufacturer || "—"}</p>
                      <p><strong>Model:</strong> {selectedJob.model?.join(", ") || "—"}</p>
                      <p><strong>Year:</strong> {selectedJob.year || "—"}</p>
                      <p><strong>Section:</strong> {selectedJob.section || "—"}</p>
                    </div>

                    <div className="jp-admin-detail-card">
                      <h3>Workflow</h3>
                      <p><strong>1.</strong> Upload PDF</p>
                      <p><strong>2.</strong> Run Mistral OCR</p>
                      {isPartsListJob(selectedJob) ? (
                        <>
                          <p><strong>3.</strong> Extract parts rows</p>
                          <p><strong>4.</strong> Search from the parts lookup page</p>
                        </>
                      ) : (
                        <>
                          <p><strong>3.</strong> Create chunks</p>
                          <p><strong>4.</strong> Publish to search</p>
                        </>
                      )}
                      <p><strong>5.</strong> Delete if needed</p>
                    </div>
                  </div>

                  <div className="jp-admin-toolbar" style={{ padding: 0, marginBottom: 0 }}>
                    <select
                      className="jp-admin-select"
                      value={previewMode}
                      onChange={(e) => setPreviewMode(e.target.value)}
                    >
                      <option value="markdown">Markdown preview</option>
                      <option value="chunks">Chunk JSON preview</option>
                    </select>
                  </div>

                  <div
                    className="jp-admin-panel"
                    style={{
                      margin: 0,
                      padding: 0,
                      overflow: "hidden",
                      background: "#0f172a",
                      borderColor: "rgba(30, 41, 59, 0.9)",
                    }}
                  >
                    <div
                      style={{
                        padding: "0.9rem 1rem",
                        borderBottom: "1px solid rgba(148, 163, 184, 0.16)",
                        color: "#cbd5e1",
                        fontSize: ".9rem",
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 16,
                      }}
                    >
                      <span>
                        {previewMode === "markdown"
                          ? "Preview of extracted markdown and diagrams"
                          : "Preview of chunk payload"}
                      </span>
                      <span>{buildChunkCount(selectedJob)} chunks</span>
                    </div>

                    <div
                      style={{
                        margin: 0,
                        padding: "1rem",
                        maxHeight: 720,
                        overflow: "auto",
                        fontSize: ".95rem",
                        lineHeight: 1.65,
                        color: "#e2e8f0",
                      }}
                    >
                      {previewMode === "markdown" ? (
                        <div className="jp-markdown-preview">
                          <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                              img: ({ src, alt, ...props }) => {
                                let finalSrc = src;

                                if (alt) {
                                  const key = alt.toLowerCase();
                                  if (visualMap.has(key)) {
                                    finalSrc = visualMap.get(key);
                                  }
                                }

                                if (!finalSrc || !String(finalSrc).startsWith("http")) {
                                  return null;
                                }

                                return (
                                  <img
                                    {...props}
                                    src={finalSrc}
                                    alt={alt || "Diagram"}
                                    style={{
                                      maxWidth: "100%",
                                      height: "auto",
                                      display: "block",
                                      margin: "1rem auto",
                                      borderRadius: 12,
                                      background: "#fff",
                                      padding: 8,
                                    }}
                                    onError={(e) => {
                                      e.currentTarget.style.display = "none";
                                    }}
                                  />
                                );
                              },
                            }}
                          >
                            {previewContent || "No preview yet. Run OCR extraction first."}
                          </ReactMarkdown>
                        </div>
                      ) : (
                        <pre
                          style={{
                            margin: 0,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                            fontFamily:
                              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                            color: "#e2e8f0",
                          }}
                        >
                          {previewContent || "No preview yet. Run OCR extraction first."}
                        </pre>
                      )}
                    </div>
                  </div>

                  {extractVisuals.length > 0 ? (
                    <div className="jp-admin-panel" style={{ margin: 0 }}>
                      <div
                        style={{
                          padding: "0.9rem 1rem",
                          borderBottom: "1px solid rgba(148, 163, 184, 0.16)",
                        }}
                      >
                        <strong>Detected visuals</strong>
                        <span className="jp-admin-muted" style={{ marginLeft: 8 }}>
                          {extractVisuals.length} preview{extractVisuals.length === 1 ? "" : "s"}
                        </span>
                      </div>

                      <div
                        style={{
                          padding: "1rem",
                          display: "grid",
                          gap: 14,
                          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                        }}
                      >
                        {extractVisuals.map((visual) => (
                          <div
                            key={visual.id}
                            style={{
                              border: "1px solid rgba(148, 163, 184, 0.18)",
                              borderRadius: 14,
                              overflow: "hidden",
                              background: "rgba(255,255,255,0.03)",
                            }}
                          >
                            {visual.imageUrl ? (
                              <img
                                src={visual.imageUrl}
                                alt={visual.title || "Extracted visual"}
                                style={{
                                  display: "block",
                                  width: "100%",
                                  height: 180,
                                  objectFit: "contain",
                                  background: "#fff",
                                }}
                              />
                            ) : null}

                            <div style={{ padding: 12 }}>
                              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                                {visual.title || `Page ${visual.pageNumber}`}
                              </div>

                              <div className="jp-admin-muted" style={{ marginBottom: 6 }}>
                                {visual.visualType || "visual"} • page {visual.pageNumber}
                              </div>

                              {visual.caption ? (
                                <div style={{ fontSize: ".92rem", lineHeight: 1.45 }}>
                                  {visual.caption}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}
            </section>
          </div>

          {showOverlay ? (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(15, 23, 42, 0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 2000,
                backdropFilter: "blur(2px)",
              }}
            >
              <div
                className="jp-admin-panel"
                style={{
                  width: "min(92vw, 420px)",
                  margin: 0,
                  textAlign: "center",
                  padding: "2rem 1.5rem",
                }}
              >
                <div
                  style={{
                    width: 42,
                    height: 42,
                    margin: "0 auto 1rem",
                    borderRadius: "999px",
                    border: "4px solid rgba(148, 163, 184, 0.25)",
                    borderTopColor: "#2563eb",
                    animation: "jpSpin 0.9s linear infinite",
                  }}
                />

                <h3 style={{ marginBottom: 8 }}>
                  {uploading
                    ? "Uploading PDF…"
                    : ocring || extracting
                    ? "Running OCR…"
                    : chunking
                    ? "Creating chunks…"
                    : publishing
                    ? "Publishing chunks…"
                    : "Deleting manual…"}
                </h3>

                <p className="jp-admin-muted" style={{ marginBottom: publishing ? 8 : 0 }}>
                  {uploading
                    ? "Sending the document to storage."
                    : ocring || extracting
                    ? "Extracting text, diagrams and tables from the manual."
                    : chunking
                    ? "Splitting the markdown into search-ready chunks."
                    : publishing
                    ? liveSelectedJob?.publishStage || "Uploading to search."
                    : "Removing PDF, extracted images, chunks, visuals and record."}
                </p>

                {publishing && publishProgressText ? (
                  <div
                    style={{
                      marginTop: 8,
                      fontWeight: 700,
                      fontSize: "1.05rem",
                      color: "#0f172a",
                      background: "rgba(255,255,255,0.92)",
                      borderRadius: 12,
                      padding: "0.6rem 0.85rem",
                      display: "inline-block",
                    }}
                  >
                    {publishProgressText}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
